import type { ReactNode } from "react";
import { Loading, Empty, ErrorState, ScreenBoundary } from "../states";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { REPORTS } from "@shared/reports.mjs";
import { cn } from "@/lib/utils";

/**
 * A report's identity, read off the one registry.
 *
 * Every report used to restate its own slug and title as `ReportFrame` props,
 * which meant the jump-nav link, the section heading and the error boundary
 * each got their wording from a different line of a different file. Now the
 * component names itself once, by slug, and the title comes from
 * `shared/reports.mjs` — the same list the harness probes.
 *
 * Throwing on an unknown slug is deliberate and safe: it happens during render,
 * so `ReportBoundary` catches it and shows one error card in place of one
 * report rather than blanking the page.
 */
export function reportMeta(slug: string): { slug: string; title: string } {
  const meta = REPORTS.find((report) => report.slug === slug);
  if (!meta) throw new Error(`No report registered under the slug "${slug}"`);
  return { slug: meta.slug, title: meta.title };
}

/**
 * One report failing must not take the page down.
 *
 * `ReportFrame` treats `data === null` as failure, but Convex's `useQuery`
 * **throws** on a server error — it never returns null. Without this, a single
 * bad report escapes to the root boundary and replaces all eight with one error
 * card, live, on a projector. The null branch alone was decoration.
 *
 * A failed report still renders its own `<section id data-report>`, so the
 * anchor links keep working and the harness can tell a broken report from a
 * missing one — and it renders inside the same `<Card>` as a working one, so a
 * failure looks like one report having a bad day rather than the page breaking.
 */
export function ReportBoundary({
  slug,
  title,
  children,
}: {
  slug: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <ScreenBoundary
      title={`${title} could not load`}
      wrap={(body) => (
        <section
          id={slug}
          data-report={slug}
          data-report-state="error"
          className={ANCHOR_OFFSET}
        >
          <Card>
            <CardHeader>
              <CardTitle>
                <h3>{title}</h3>
              </CardTitle>
            </CardHeader>
            <CardContent>{body}</CardContent>
          </Card>
        </section>
      )}
      // Keep it in the console for whoever is debugging, without putting a
      // stack trace on screen in front of the client.
      onError={(error, info) =>
        console.error(`Report "${slug}" failed:`, error, info.componentStack)
      }
    >
      {children}
    </ScreenBoundary>
  );
}

/**
 * How far an anchor jump stops short of the top.
 *
 * The shell's header is sticky at every width, and the reports index sticks
 * underneath it. A jump that lands the card title behind either of them is a
 * jump that looks like it did nothing — and both heights vary (safe-area inset,
 * a second nav strip on phones). So the Reports page measures them and
 * publishes the sum as `--report-anchor`; the fallback covers a report rendered
 * outside that page.
 */
const ANCHOR_OFFSET = "scroll-mt-[var(--report-anchor,6rem)]";

/**
 * The frame every report renders inside — one stock `<Card>`.
 *
 * Header, description, body, footnote map onto the registry's own
 * `CardHeader` / `CardTitle` / `CardDescription` / `CardContent` / `CardFooter`
 * slots, so a Nexora report and a shadcn dashboard card are the same object.
 * There is exactly one card per report and **no card inside a card**: the
 * blocks within a report separate themselves with rules and spacing, because
 * a white box on a white box was the single loudest thing wrong with this page.
 *
 * The `<h3>` inside `CardTitle` is semantics, not styling — Tailwind's preflight
 * resets heading size and weight to `inherit`, so it draws exactly as the
 * registry's title does. The page's `<h1>` is the screen title and each group's
 * `<h2>` is above this, so a screen reader walking eight reports hears them
 * nested under the group they belong to rather than as sixteen flat siblings.
 *
 * `data-report` and `data-report-state` are load-bearing. `tools/demo.mjs`
 * asserts on them, and it asserts on `state="ready"` specifically — a selector
 * that matches while a query is still in flight is how checkbox 7 previously
 * reported a pass for a screen that had rendered nothing. The state attribute
 * cannot be true until data has arrived, because it is derived from the data.
 *
 * The `data === null` branch below is a belt-and-braces path. A thrown query is
 * caught by `ReportBoundary` above, which is what actually keeps one broken
 * report from taking the other seven with it.
 */
export function ReportFrame({
  slug,
  title,
  question,
  data,
  count,
  empty,
  children,
  footnote,
}: {
  /** Stable id. Used by the harness and by the in-page anchor links. */
  slug: string;
  title: string;
  /** The question this report answers, in the words someone would ask it. */
  question: string;
  /** `undefined` while loading, `null` on failure. */
  data: unknown;
  /** Rows in the result, so an empty report is distinguishable from a broken one. */
  count?: number;
  empty?: { title: string; body?: string; good?: boolean };
  children: ReactNode;
  footnote?: ReactNode;
}) {
  const state =
    data === undefined ? "loading" : data === null ? "error" : "ready";

  return (
    <section
      id={slug}
      data-report={slug}
      data-report-state={state}
      data-report-rows={count ?? ""}
      className={ANCHOR_OFFSET}
    >
      <Card>
        <CardHeader>
          <CardTitle>
            <h3>{title}</h3>
          </CardTitle>
          <CardDescription>{question}</CardDescription>
        </CardHeader>

        <CardContent>
          {state === "loading" ? (
            <Loading rows={2} label={`Loading ${title}`} />
          ) : state === "error" ? (
            <ErrorState
              title={`${title} could not load`}
              body="This report failed to run. The other reports on this page are unaffected."
              onRetry={() => window.location.reload()}
            />
          ) : count === 0 && empty ? (
            <Empty
              title={empty.title}
              body={empty.body}
              tone={empty.good ? "good" : "neutral"}
            />
          ) : (
            children
          )}
        </CardContent>

        {/* `border-t` is what makes `CardFooter` pad itself off the body — the
            registry's own `[.border-t]:pt-6`. The paragraph is required rather
            than tidy: a footnote is often a fragment, and bare text nodes in a
            flex footer would lay themselves out side by side. */}
        {footnote && state === "ready" ? (
          <CardFooter className="border-t">
            <p className="text-xs leading-relaxed text-muted-foreground">
              {footnote}
            </p>
          </CardFooter>
        ) : null}
      </Card>
    </section>
  );
}

/**
 * The strip of figures above a report's rows.
 *
 * The answer to the report's question before anyone reads a row — how many, how
 * much, how long. Red because every report that carries one is a list of things
 * that still need doing, which is what red means on the sheets staff read
 * today. Children are the figures; the strip owns the colour and the wrapping,
 * so the low-stock and back-order summaries cannot drift apart.
 *
 * A border rather than a ring, and the registry radius: it is a callout inside
 * a card, not a second card.
 */
export function ReportSummaryStrip({ children }: { children: ReactNode }) {
  return (
    <div className="mb-3 flex flex-wrap items-baseline gap-x-4 gap-y-1 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
      {children}
    </div>
  );
}

/**
 * The two-to-four figures a report leads with.
 *
 * The children are `kit.tsx`'s `<Tile size="sm">` — the same component the
 * dashboard's figures are, rather than a second stat implementation that
 * happened to agree with it. The grid only sets the columns and the gutter; the
 * tiles bring their own edge, tone stripe and `NumberFlow` counter.
 */
export function ReportStats({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-3">{children}</div>;
}

/**
 * A plain row used by most of the reports: a label, a detail line, a figure.
 *
 * Two tones, not three. It offered amber as well, and no report ever passed it
 * — every report that carries a tone is a list of things that still need
 * doing, which is what red means on the sheets staff read today. An amber row
 * would have been a fourth status colour in a language that has three.
 */
export function ReportRow({
  label,
  detail,
  value,
  tone = "neutral",
}: {
  label: string;
  detail?: ReactNode;
  value: ReactNode;
  tone?: "neutral" | "red";
}) {
  return (
    <div
      className={cn(
        "flex items-start justify-between gap-3 border-b py-2 last:border-0",
        tone === "red" && "text-red-900",
      )}
    >
      <div className="min-w-0">
        <div className="truncate text-sm font-medium">{label}</div>
        {detail ? (
          <div className="truncate text-xs text-muted-foreground">{detail}</div>
        ) : null}
      </div>
      <div className="num shrink-0 text-sm font-medium">{value}</div>
    </div>
  );
}
